// src/pages/ReportsPage.jsx — v9.4 (Incident Reports)
// Patch 9: Renamed "Reports" → "Incident Reports". Improved hierarchy, spacing, labels.
// Lightweight localStorage history — no backend required.
//
// Data shape per entry:
//   { id, savedAt, fileName, root_cause, estimated_loss, peak_date, recovery_date,
//     days_total, anomaly_days, summary }
//
// Integration: call saveReport(result, fileName) from AnalyzePage after analysis.

import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";

const STORAGE_KEY = "viraj_reports";
const MONO        = "var(--font-mono)";

// ── Root cause labels ─────────────────────────────────────────────────────────
const ROOT_CAUSE_LABELS = {
  CR_DROPPED:        "Conversion rate collapsed",
  SPEND_SPIKE:       "Traffic cost spiked",
  SPEND_DROP:        "Spend dropped sharply",
  ATTRIBUTION_DELAY: "Attribution data delayed",
  REVENUE_DROP:      "Revenue fell unexpectedly",
  ROI_DEGRADED:      "Return on spend degraded",
  MULTI_SIGNAL:      "Multiple signals deteriorated",
};

const SEVERITY_COLORS = {
  critical: "var(--danger)",
  high:     "var(--danger)",
  medium:   "var(--warning)",
  low:      "var(--text-muted)",
};

// ── Public API — call this from AnalyzePage ───────────────────────────────────
export function saveReport(result, fileName) {
  if (!result?.ok || result.no_anomaly) return;
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    const entry = {
      id:             (typeof crypto !== "undefined" && crypto.randomUUID?.()) || String(Date.now()),
      savedAt:        new Date().toISOString(),
      fileName:       fileName ?? "Unknown file",
      root_cause:     result.attribution?.root_cause ?? "MULTI_SIGNAL",
      risk_status:    result.risk?.status             ?? "high",
      estimated_loss: result.stats?.estimated_loss    ?? 0,
      peak_date:      result.stats?.peak_date         ?? null,
      recovery_date:  result.stats?.recovery_date     ?? null,
      days_total:     result.stats?.days_total        ?? 0,
      anomaly_days:   result.stats?.anomaly_days      ?? 0,
      summary:        result.narrative?.summary       ?? "",
    };
    const updated = [entry, ...existing].slice(0, 50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch { /* localStorage unavailable */ }
}

// ── Incident card ─────────────────────────────────────────────────────────────
function IncidentCard({ report, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  const savedDate  = new Date(report.savedAt).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const humanCause = ROOT_CAUSE_LABELS[report.root_cause] ?? report.root_cause ?? "Unknown cause";
  const hasLoss    = (report.estimated_loss ?? 0) > 0;
  const sevColor   = SEVERITY_COLORS[report.risk_status] ?? "var(--danger)";

  return (
    <div style={{
      borderRadius: "var(--radius)",
      background:   "var(--bg-card)",
      border:       `1px solid var(--border-subtle)`,
      overflow:     "hidden",
    }}>
      {/* Main row */}
      <div
        onClick={() => setExpanded(x => !x)}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "16px 20px", cursor: "pointer",
        }}
      >
        {/* Loss */}
        <div style={{
          flexShrink:  0,
          padding:     "5px 10px",
          borderRadius:"var(--radius-sm)",
          background:  hasLoss ? "var(--danger-bg)" : "var(--bg-elevated)",
          border:      hasLoss ? "1px solid var(--danger-border)" : "1px solid var(--border-subtle)",
          fontFamily:  MONO, fontSize: 12, fontWeight: 700,
          color:       hasLoss ? "var(--danger)" : "var(--text-muted)",
          minWidth:    72, textAlign: "center",
        }}>
          {hasLoss ? `€ ${report.estimated_loss.toLocaleString("en-US")}` : "—"}
        </div>

        {/* Cause + metadata */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: MONO, fontSize: 13, fontWeight: 600, color: sevColor,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            marginBottom: 3,
          }}>
            {humanCause}
          </div>
          <div style={{
            fontFamily: MONO, fontSize: 10, color: "var(--text-muted)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {report.fileName}
            {" · "}
            {report.days_total} days analyzed
            {report.anomaly_days > 0 && ` · ${report.anomaly_days} incident day${report.anomaly_days !== 1 ? "s" : ""}`}
          </div>
        </div>

        {/* Dates column */}
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          {report.peak_date && (
            <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)" }}>
              {report.peak_date}
            </div>
          )}
          {report.recovery_date && (
            <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--success)", marginTop: 2 }}>
              ↑ recovered {report.recovery_date}
            </div>
          )}
        </div>

        {/* Saved date */}
        <div style={{
          flexShrink: 0, fontFamily: MONO, fontSize: 10,
          color: "var(--text-tertiary)", minWidth: 90, textAlign: "right",
        }}>
          {savedDate}
        </div>

        {/* Expand chevron */}
        <span style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", flexShrink: 0 }}>
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: "0 20px 16px", borderTop: "1px solid var(--border-subtle)" }}>
          {report.summary && (
            <div style={{
              fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)",
              lineHeight: 1.7, paddingTop: 14, marginBottom: 14,
            }}>
              {report.summary}
            </div>
          )}
          <button
            onClick={e => { e.stopPropagation(); onDelete(report.id); }}
            style={{
              fontFamily: MONO, fontSize: 10, cursor: "pointer",
              padding: "4px 11px", borderRadius: "var(--radius-sm)",
              background: "transparent", border: "1px solid var(--border-subtle)",
              color: "var(--text-muted)",
            }}
          >
            Remove from history
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);

  useEffect(() => {
    try {
      setReports(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]"));
    } catch {
      setReports([]);
    }
  }, []);

  function handleDelete(id) {
    const updated = reports.filter(r => r.id !== id);
    setReports(updated);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch { /**/ }
  }

  function handleClearAll() {
    setReports([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /**/ }
  }

  const totalLoss  = reports.reduce((s, r) => s + (r.estimated_loss ?? 0), 0);
  const withRecov  = reports.filter(r => r.recovery_date).length;

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: "var(--text-primary)", marginBottom: 5 }}>
            Incident Reports
          </div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-muted)" }}>
            Saved forensic analyses from previous CSV uploads · stored locally in your browser
          </div>
        </div>
        {reports.length > 0 && (
          <button
            onClick={handleClearAll}
            style={{
              fontFamily: MONO, fontSize: 10, cursor: "pointer",
              padding: "6px 13px", borderRadius: "var(--radius-sm)",
              background: "transparent", border: "1px solid var(--border-subtle)",
              color: "var(--text-muted)",
            }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Summary metrics */}
      {reports.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
          {[
            { label: "Incidents saved",        value: reports.length,                                       color: "var(--text-primary)" },
            { label: "Total avoidable losses",  value: `€ ${totalLoss.toLocaleString("en-US")}`,            color: totalLoss > 0 ? "var(--danger)" : "var(--text-muted)" },
            { label: "With detected recovery",  value: withRecov,                                            color: withRecov > 0 ? "var(--success)" : "var(--text-muted)" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              padding: "12px 18px", borderRadius: "var(--radius)",
              background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
              flex: "1 1 160px",
            }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.10em",
                            textTransform: "uppercase", marginBottom: 5 }}>{label}</div>
              <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Report list */}
      {reports.length === 0 ? (
        <div style={{
          padding: "52px 36px", borderRadius: "var(--radius)",
          background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
          textAlign: "center",
        }}>
          <div style={{ fontFamily: MONO, fontSize: 24, marginBottom: 14, opacity: 0.25 }}>◎</div>
          <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>
            No incident reports saved yet
          </div>
          <div style={{
            fontFamily: MONO, fontSize: 11, color: "var(--text-muted)",
            lineHeight: 1.7, marginBottom: 24, maxWidth: 360, margin: "0 auto 24px",
          }}>
            After analyzing a CSV that contains a performance incident, it will appear here.
            Reports are stored locally — nothing is sent to any server.
          </div>
          <button
            onClick={() => navigate("/analyze")}
            style={{
              fontFamily: MONO, fontSize: 12, fontWeight: 700, cursor: "pointer",
              padding: "10px 22px", borderRadius: "var(--radius-sm)",
              background: "var(--accent-primary)", color: "#fff", border: "none",
            }}
          >
            Analyze a campaign →
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {reports.map(r => (
            <IncidentCard key={r.id} report={r} onDelete={handleDelete} />
          ))}
        </div>
      )}

    </div>
  );
}
